import React from 'react';

export type SkeletonProps = React.HTMLAttributes<HTMLSpanElement> & {
  /** Tailwind sizing utilities, e.g. "h-4 w-32". */
  className?: string;
  shape?: 'line' | 'block' | 'circle';
};

const SHAPE_CLASSES = {
  line: 'rounded-full',
  block: 'rounded-card',
  circle: 'rounded-full aspect-square',
} as const;

/**
 * Placeholder for content whose shape is known before its data arrives.
 *
 * The catalog's loading guidance is to "preserve the page skeleton", so this is
 * sized by the call site rather than guessing. Always paired with an accessible
 * loading announcement from the surrounding EmptyState or region — a skeleton on
 * its own is invisible to screen readers by design (`aria-hidden`).
 */
export function Skeleton({ className = '', shape = 'line', ...props }: SkeletonProps) {
  return (
    <span
      aria-hidden="true"
      className={`block animate-pulse bg-line motion-reduce:animate-none ${SHAPE_CLASSES[shape]} ${className}`}
      {...props}
    />
  );
}

/** Convenience for the common "a few lines of text" placeholder. */
export function SkeletonText({ lines = 3, className = '' }: { lines?: number; className?: string }) {
  return (
    <span className={`flex flex-col gap-2 ${className}`}>
      {Array.from({ length: lines }, (_, index) => (
        <Skeleton key={index} className={`h-3 ${index === lines - 1 ? 'w-2/3' : 'w-full'}`} />
      ))}
    </span>
  );
}

export default Skeleton;
